import { runFamilyAHalftone } from './family-a/halftone';
import { gaussianBlur } from './family-b/gaussian-blur';
import { patternThresholdRender, patternToneRender } from './family-b/pattern-threshold';
import { runFamilyCTransform } from './family-c/transform';
import type { DitherGpuBackend, GpuRegionBatchRequest } from './gpu/backend';
import type { GpuRenderDiagnostics, RenderBackend } from './gpu/diagnostics';
import type {
  CustomPatternParams,
  DitherInput,
  DitherOptions,
  DitherResult,
  MethodParams,
  Region,
} from './types';
import { computeLabelBbox, cropImageData } from './util/bbox';
import { toGrayscale } from './util/grayscale';
import { applySourceAdjust } from './util/source-adjust';

/**
 * Coordinate offset used by the multi-region pipeline so per-pixel coord
 * math (cell tiling, jitter sampling, grid rotation around the image
 * centre, Family B pattern tiling) keeps using full-image positions even
 * though we iterate over a cropped buffer. Internal - never exported.
 */
interface RegionOrigin {
  originX: number;
  originY: number;
  fullW: number;
  fullH: number;
  labelMap?: ImageData;
  label?: number;
}


/**
 * Maps the runtime backend name to the corresponding RenderBackend discriminant
 * used in GpuRenderDiagnostics. Kept here so the catch-block fallback
 * diagnostic derives its `attemptedBackend` from `options.backend.name`
 * rather than a hard-coded literal, ensuring B0.2+ backends (WebGPU,
 * Metal-native) report correctly in diagnostics.
 */
function backendNameToRenderBackend(
  name: DitherGpuBackend['name'],
): Extract<RenderBackend, 'gpu-webgl2' | 'gpu-webgpu' | 'gpu-metal-native'> {
  switch (name) {
    case 'webgl2': return 'gpu-webgl2';
    case 'webgpu': return 'gpu-webgpu';
    case 'metal-native': return 'gpu-metal-native';
  }
}

function runFamilyB(
  source: ImageData,
  method: CustomPatternParams,
  origin?: RegionOrigin,
): ImageData {
  // When isGrayscale is false the pattern may be colour line-art. Convert via
  // Rec. 601 luma so all three channels contribute; downstream operations
  // (gaussianBlur, applySourceAdjust, patternThresholdRender) only read .R.
  let pattern = method.isGrayscale ? method.pattern : toGrayscale(method.pattern);
  if (!method.isGrayscale && method.blurRadius > 0) {
    pattern = gaussianBlur(pattern, method.blurRadius);
  }
  // patternInvert handling is mode-dependent. In threshold mode the
  // per-pixel invert in the renderer pairs with the source-vs-pattern
  // threshold compare in a way that already gives intuitive brightness
  // direction ("+ brightness on pattern → output brighter"), so we
  // leave invert per-pixel for that path. In compose-group modes
  // (Screen / Overlay / Soft Light / Pin Light) the per-pixel invert
  // would flip pattern values AFTER brightness/contrast at pipeline
  // time, reversing the slider direction - so for those modes we
  // invert ONCE at the pipeline layer before applySourceAdjust, then
  // tell the renderer to skip its own per-pixel invert. Multiply uses
  // the same path so its invert toggle remains visible and meaningful.
  const blendMode = method.blendMode ?? 'threshold';
  const invertAtPipeline =
    (method.patternInvert ?? false) &&
    (blendMode === 'multiply' ||
      blendMode === 'screen' ||
      blendMode === 'overlay' ||
      blendMode === 'softlight' ||
      blendMode === 'pinlight');
  if (invertAtPipeline) {
    pattern = invertGrayBytes(pattern);
  }
  pattern = applySourceAdjust(pattern, {
    brightness: method.patternBrightness ?? 0,
    contrast: method.patternContrast ?? 0,
    gamma: 1,
  });
  // If we already invert-consumed at the pipeline, hide it from the
  // renderer; otherwise pass it through for per-pixel handling
  // (threshold mode's intended path).
  const passInvert = invertAtPipeline ? false : (method.patternInvert ?? false);
  const postprocess = method.halftonePostprocess;
  const patternOutput = postprocess?.enabled
    ? patternToneRender
    : patternThresholdRender;
  const output = patternOutput(
    source,
    pattern,
    { ...method, patternInvert: passInvert },
    origin ? { originX: origin.originX, originY: origin.originY } : undefined,
  );
  return runFamilyBHalftonePostprocess(output, method, origin);
}

function runFamilyBHalftonePostprocess(
  output: ImageData,
  method: CustomPatternParams,
  origin?: RegionOrigin,
): ImageData {
  const postprocess = method.halftonePostprocess;
  if (!postprocess?.enabled) {
    return output;
  }

  return runFamilyAHalftone(
    output,
    {
      family: 'A',
      cellSize: postprocess.cellSize,
      stamp: postprocess.stamp,
      polarity: postprocess.polarity,
      density: postprocess.density,
      blackThreshold: normalisePostprocessBlackThreshold(postprocess.blackThreshold),
      whiteThreshold: normalisePostprocessWhiteThreshold(postprocess.whiteThreshold),
      gridAngleDeg: postprocess.gridAngleDeg ?? 0,
      shapeAngleDeg: postprocess.shapeAngleDeg ?? 0,
      shapeFlipX: postprocess.shapeFlipX ?? false,
      shapeFlipY: postprocess.shapeFlipY ?? false,
      algorithmJitter: postprocess.algorithmJitter ?? 0.075,
      patternNoiseScale: postprocess.patternNoiseScale ?? method.patternNoiseScale ?? 1,
    },
    origin,
  );
}

function normalisePostprocessBlackThreshold(value: number | undefined): number {
  return value === undefined || value <= 0 ? -Number.EPSILON : value;
}

function normalisePostprocessWhiteThreshold(value: number | undefined): number {
  return value === undefined || value >= 1 ? 1 + Number.EPSILON : value;
}

function familyAMethodFromPostprocess(method: CustomPatternParams): MethodParams | null {
  const postprocess = method.halftonePostprocess;
  if (!postprocess?.enabled) return null;
  return {
    family: 'A',
    algorithm: 'bluenoise',
    cellSize: postprocess.cellSize,
    stamp: postprocess.stamp,
    polarity: postprocess.polarity,
    density: postprocess.density,
    blackThreshold: normalisePostprocessBlackThreshold(postprocess.blackThreshold),
    whiteThreshold: normalisePostprocessWhiteThreshold(postprocess.whiteThreshold),
    gridAngleDeg: postprocess.gridAngleDeg ?? 0,
    shapeAngleDeg: postprocess.shapeAngleDeg ?? 0,
    shapeFlipX: postprocess.shapeFlipX ?? false,
    shapeFlipY: postprocess.shapeFlipY ?? false,
    algorithmJitter: postprocess.algorithmJitter ?? 0.075,
  };
}

function invertGrayBytes(img: ImageData): ImageData {
  const out = new ImageData(img.width, img.height);
  const sd = img.data;
  const dd = out.data;
  for (let i = 0; i < sd.length; i += 4) {
    dd[i] = 255 - (sd[i] ?? 0);
    dd[i + 1] = 255 - (sd[i + 1] ?? 0);
    dd[i + 2] = 255 - (sd[i + 2] ?? 0);
    dd[i + 3] = sd[i + 3] ?? 255;
  }
  return out;
}

// ---------------------------------------------------------------------------
// B0.6 frame-level GPU route planner.
// ---------------------------------------------------------------------------

export interface FrameRoutePlan {
  gpuRegionLabels: number[];
  cpuRegionLabels: number[];
  reasonByLabel: Map<number, string>;
  defaultBackend: 'cpu' | 'gpu-webgl2' | 'mixed';
}

type GpuSupport =
  | { ok: true }
  | { ok: false; reason: string };

type RoutePlannerOptions = {
  readonly backend?: DitherGpuBackend;
  readonly __routeOverride?: 'all' | 'none';
  readonly labelMap?: ImageData;
};

const FAMILY_C_WERNESS_MAX_GPU_CELLS = 16384;

function snapCeil(value: number): number {
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) < 1e-9) return rounded;
  return Math.ceil(value);
}

function rotatedFamilyCDimensions(
  sourceWidth: number,
  sourceHeight: number,
  angleDeg: number,
): { rotatedWidth: number; rotatedHeight: number } {
  const normalised = ((angleDeg % 360) + 360) % 360;
  const rad = (normalised * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    rotatedWidth: Math.max(
      1,
      snapCeil(Math.abs(sourceWidth * cos) + Math.abs(sourceHeight * sin)),
    ),
    rotatedHeight: Math.max(
      1,
      snapCeil(Math.abs(sourceWidth * sin) + Math.abs(sourceHeight * cos)),
    ),
  };
}

function classifyRegionForGpu(
  method: MethodParams,
  caps: ReturnType<DitherGpuBackend['getCaps']> | null,
  sourceWidth: number,
  sourceHeight: number,
  hasLabelMap: boolean,
): GpuSupport {
  if (method.family === 'B') {
    if (!method.isGrayscale && method.blurRadius !== 0) {
      return {
        ok: false,
        reason: 'Family B GPU blur preprocessing lands after B3',
      };
    }
    const postprocessMethod = familyAMethodFromPostprocess(method);
    if (postprocessMethod !== null) {
      const support = classifyRegionForGpu(
        postprocessMethod,
        caps,
        sourceWidth,
        sourceHeight,
        hasLabelMap,
      );
      if (!support.ok) return support;
    }
    if (
      caps !== null &&
      (method.pattern.width > caps.maxTextureSize ||
        method.pattern.height > caps.maxTextureSize)
    ) {
      return {
        ok: false,
        reason: `pattern ${method.pattern.width}x${method.pattern.height} exceeds maxTextureSize ${caps.maxTextureSize}`,
      };
    }
    return { ok: true };
  }

  if (method.family === 'C') {
    if (hasLabelMap) {
      return {
        ok: false,
        reason: 'Family C label-map GPU lands after B4',
      };
    }
    if (!Number.isInteger(method.cellSize)) {
      return {
        ok: false,
        reason: 'cellSize must be integer for v0.2.5.0 B0',
      };
    }
    if (method.cellSize < 1 || method.cellSize > 64) {
      return {
        ok: false,
        reason: `cellSize ${method.cellSize} out of range [1, 64]`,
      };
    }
    if (!Number.isFinite(method.density) || method.density < 0 || method.density > 100) {
      return {
        ok: false,
        reason: `density ${method.density} out of range [0, 100]`,
      };
    }
    if (!Number.isFinite(method.gridAngleDeg ?? 0)) {
      return {
        ok: false,
        reason: `gridAngleDeg ${String(method.gridAngleDeg)} is not finite`,
      };
    }
    if (!Number.isFinite(method.shapeAngleDeg ?? 0)) {
      return {
        ok: false,
        reason: `shapeAngleDeg ${String(method.shapeAngleDeg)} is not finite`,
      };
    }
    const grainStrength = method.grainStrength ?? 0;
    if (!Number.isFinite(grainStrength) || grainStrength < 0 || grainStrength > 1) {
      return {
        ok: false,
        reason: `grainStrength ${String(method.grainStrength)} out of range [0, 1]`,
      };
    }
    const grainScale = method.grainScale ?? 1;
    if (!Number.isFinite(grainScale) || grainScale < 1 || grainScale > 16) {
      return {
        ok: false,
        reason: `grainScale ${String(method.grainScale)} out of range [1, 16]`,
      };
    }
    if (method.algorithm !== 'bayer' && method.algorithm !== 'bluenoise' && method.algorithm !== 'werness') {
      return {
        ok: false,
        reason: `${method.algorithm} Family C GPU lands after B4`,
      };
    }
    if (
      method.algorithm === 'bayer' &&
      method.matrixSize !== 2 &&
      method.matrixSize !== 4 &&
      method.matrixSize !== 8 &&
      method.matrixSize !== 16
    ) {
      return {
        ok: false,
        reason: `matrixSize ${method.matrixSize} unsupported on GPU B4`,
      };
    }
    const rotated = rotatedFamilyCDimensions(
      sourceWidth,
      sourceHeight,
      -(method.gridAngleDeg ?? 0),
    );
    const cellWidth = Math.floor(rotated.rotatedWidth / method.cellSize);
    const cellHeight = Math.floor(rotated.rotatedHeight / method.cellSize);
    if (cellWidth < 1 || cellHeight < 1) {
      return {
        ok: false,
        reason: 'Family C rotated cell grid is empty',
      };
    }
    if (
      caps !== null &&
      (cellWidth > caps.maxTextureSize ||
        cellHeight > caps.maxTextureSize ||
        cellWidth > caps.maxRenderbufferSize ||
        cellHeight > caps.maxRenderbufferSize)
    ) {
      return {
        ok: false,
        reason: `Family C cell mask ${cellWidth}x${cellHeight} exceeds GPU target limits`,
      };
    }
    if (method.algorithm === 'werness') {
      const cellCount = cellWidth * cellHeight;
      if (cellCount > FAMILY_C_WERNESS_MAX_GPU_CELLS) {
        return {
          ok: false,
          reason: `Family C Werness GPU cell count ${cellCount} exceeds B4 proof cap ${FAMILY_C_WERNESS_MAX_GPU_CELLS}`,
        };
      }
      if (caps !== null && !caps.extensions.includes('EXT_color_buffer_float')) {
        return {
          ok: false,
          reason: 'EXT_color_buffer_float unavailable for Family C Werness GPU',
        };
      }
    }
    if (method.stamp.type === 'custom') {
      if (method.stamp.mask.width < 1 || method.stamp.mask.height < 1) {
        return {
          ok: false,
          reason: 'custom stamp mask must be at least 1x1',
        };
      }
      if (
        caps !== null &&
        (method.stamp.mask.width > caps.maxTextureSize ||
          method.stamp.mask.height > caps.maxTextureSize)
      ) {
        return {
          ok: false,
          reason: `custom stamp ${method.stamp.mask.width}x${method.stamp.mask.height} exceeds maxTextureSize ${caps.maxTextureSize}`,
        };
      }
    }
    if (caps?.precisionFormat !== 'highp_supported') {
      return {
        ok: false,
        reason: 'fragment highp precision unavailable',
      };
    }
    if (
      caps !== null &&
      (sourceWidth > caps.maxTextureSize ||
        sourceHeight > caps.maxTextureSize ||
        sourceWidth > caps.maxRenderbufferSize ||
        sourceHeight > caps.maxRenderbufferSize)
    ) {
      return {
        ok: false,
        reason: `source ${sourceWidth}x${sourceHeight} exceeds GPU target limits`,
      };
    }
    return { ok: true };
  }

  if (!Number.isInteger(method.cellSize)) {
    return {
      ok: false,
      reason: 'cellSize must be integer for v0.2.5.0 B0',
    };
  }
  if (method.cellSize < 1 || method.cellSize > 64) {
    return {
      ok: false,
      reason: `cellSize ${method.cellSize} out of range [1, 64]`,
    };
  }

  if (!Number.isFinite(method.density) || method.density < 0 || method.density > 100) {
    return {
      ok: false,
      reason: `density ${method.density} out of range [0, 100]`,
    };
  }

  if (method.polarity !== 'black' && method.polarity !== 'white') {
    return {
      ok: false,
      reason: `polarity '${method.polarity}' must be 'black' or 'white'`,
    };
  }

  if (!Number.isFinite(method.gridAngleDeg ?? 0)) {
    return {
      ok: false,
      reason: `gridAngleDeg ${String(method.gridAngleDeg)} is not finite`,
    };
  }

  if (method.algorithm === 'bayer') {
    if (method.matrixSize !== 2 && method.matrixSize !== 4 && method.matrixSize !== 8) {
      return {
        ok: false,
        reason: `matrixSize ${method.matrixSize} unsupported on GPU B2`,
      };
    }
    if (
      method.stamp.type !== 'pixel' &&
      method.matrixSize === 2
    ) {
      return {
        ok: false,
        reason: 'matrixSize 2 analytic stamps require B2 matrixSize 4 or 8',
      };
    }
  }

  if (method.stamp.type === 'custom') {
    if (method.stamp.mask.width < 1 || method.stamp.mask.height < 1) {
      return {
        ok: false,
        reason: 'custom stamp mask must be at least 1x1',
      };
    }
    if (
      caps !== null &&
      (method.stamp.mask.width > caps.maxTextureSize ||
        method.stamp.mask.height > caps.maxTextureSize)
    ) {
      return {
        ok: false,
        reason: `custom stamp ${method.stamp.mask.width}x${method.stamp.mask.height} exceeds maxTextureSize ${caps.maxTextureSize}`,
      };
    }
  }

  if (caps?.precisionFormat !== 'highp_supported') {
    return {
      ok: false,
      reason: 'fragment highp precision unavailable',
    };
  }

  if (
    caps !== null &&
    (sourceWidth > caps.maxTextureSize || sourceHeight > caps.maxTextureSize)
  ) {
    return {
      ok: false,
      reason: `source ${sourceWidth}x${sourceHeight} exceeds maxTextureSize ${caps.maxTextureSize}`,
    };
  }

  return { ok: true };
}

export function planFrameRoute(
  regions: ReadonlyArray<Region>,
  source: ImageData,
  options: RoutePlannerOptions = {},
): FrameRoutePlan {
  if (options.__routeOverride === 'all') {
    return {
      gpuRegionLabels: regions.map(r => r.label),
      cpuRegionLabels: [],
      reasonByLabel: new Map(),
      defaultBackend: 'gpu-webgl2',
    };
  }

  if (options.__routeOverride === 'none' || options.backend === undefined) {
    const reason =
      options.__routeOverride === 'none'
        ? 'GPU route forced to CPU by test override'
        : 'no GPU backend provided';
    const reasonByLabel = new Map<number, string>();
    for (const region of regions) {
      reasonByLabel.set(region.label, reason);
    }
    return {
      gpuRegionLabels: [],
      cpuRegionLabels: regions.map(r => r.label),
      reasonByLabel,
      defaultBackend: 'cpu',
    };
  }

  if (!options.backend.isReady()) {
    const reasonByLabel = new Map<number, string>();
    for (const region of regions) {
      reasonByLabel.set(region.label, 'GPU backend not ready');
    }
    return {
      gpuRegionLabels: [],
      cpuRegionLabels: regions.map(r => r.label),
      reasonByLabel,
      defaultBackend: 'cpu',
    };
  }

  const caps = options.backend.getCaps();
  const gpuRegionLabels: number[] = [];
  const cpuRegionLabels: number[] = [];
  const reasonByLabel = new Map<number, string>();

  for (const region of regions) {
    const support = classifyRegionForGpu(
      region.method,
      caps,
      source.width,
      source.height,
      options.labelMap !== undefined,
    );
    if (support.ok) {
      gpuRegionLabels.push(region.label);
    } else {
      cpuRegionLabels.push(region.label);
      reasonByLabel.set(region.label, support.reason);
    }
  }

  const defaultBackend =
    gpuRegionLabels.length === 0
      ? 'cpu'
      : cpuRegionLabels.length === 0
        ? 'gpu-webgl2'
        : 'mixed';

  return {
    gpuRegionLabels,
    cpuRegionLabels,
    reasonByLabel,
    defaultBackend,
  };
}

async function runMethodOnBuffer(
  source: ImageData,
  method: MethodParams,
  options: DitherOptions | undefined,
  origin?: RegionOrigin,
): Promise<{ output: ImageData; processedW: number; processedH: number }> {
  // Family A is the cell-average blue-noise halftone renderer.
  // Family C is the new pixel-to-dot transform pipeline (rotate
  // source → downsample → algorithm → render dots → unrotate).
  // Family B is the uploaded-image overlay.
  if (method.family === 'A') {
    return {
      output: runFamilyAHalftone(source, method, origin),
      processedW: source.width,
      processedH: source.height,
    };
  }
  if (method.family === 'C') {
    return {
      output: runFamilyCTransform(source, method, origin),
      processedW: source.width,
      processedH: source.height,
    };
  }
  void options;
  const output = runFamilyB(source, method, origin);
  return { output, processedW: source.width, processedH: source.height };
}

/**
 * Item 25 - alpha=0 post-pass (plan A8).
 *
 * After the multi-region compositing loop, zero the alpha channel for every
 * pixel whose label (red channel of the labelMap) is 0. Label-0 pixels are
 * unlabelled or represent deleted regions; making them transparent lets the
 * caller (PNG export, canvas preview) composite them correctly.
 *
 * Only called from the multi-region path; the single-region path has no
 * labelMap and does not invoke this function.
 *
 * Plan A8 contract: ONLY label-0 pixels become transparent. Pixels with
 * non-zero labels that have no matching region remain opaque white (the
 * existing default initialisation), which is tested in the "unknown labels
 * stay white" test.
 */
function applyAlphaForLabelZero(
  output: ImageData,
  labelData: Uint8ClampedArray,
  outW: number,
  outH: number,
): void {
  const total = outW * outH;
  for (let p = 0; p < total; p++) {
    const base = p * 4;
    // labelData is the raw RGBA of the labelMap; red channel (base+0) holds
    // the label index. Label 0 = unlabelled / deleted.
    if (labelData[base] === 0) {
      output.data[base + 3] = 0;
    }
  }
}

async function compositeCpuLabelRegions(
  output: ImageData,
  source: ImageData,
  labelMap: ImageData,
  regions: ReadonlyArray<Region>,
  options: DitherOptions | undefined,
): Promise<void> {
  const outW = source.width;
  const outH = source.height;
  const labelData = labelMap.data;
  for (let r = 0; r < regions.length; r++) {
    const region = regions[r]!;
    const bbox = computeLabelBbox(labelMap, region.label);
    if (!bbox) continue;

    const cropped = cropImageData(source, bbox);
    const result = await runMethodOnBuffer(cropped, region.method, options, {
      originX: bbox.x,
      originY: bbox.y,
      fullW: outW,
      fullH: outH,
      labelMap,
      label: region.label,
    });

    const targetLabel = region.label;
    const regionOutput = result.output.data;
    const bboxW = bbox.w;
    for (let by = 0; by < bbox.h; by++) {
      const fullRow = (bbox.y + by) * outW;
      const cropRow = by * bboxW;
      for (let bx = 0; bx < bboxW; bx++) {
        const labelIdx = (fullRow + bbox.x + bx) * 4;
        if (labelData[labelIdx] !== targetLabel) continue;
        const cropIdx = (cropRow + bx) * 4;
        output.data[labelIdx] = regionOutput[cropIdx]!;
        output.data[labelIdx + 1] = regionOutput[cropIdx + 1]!;
        output.data[labelIdx + 2] = regionOutput[cropIdx + 2]!;
        output.data[labelIdx + 3] = 255;
      }
    }
  }
}

export async function dither(
  input: DitherInput,
  options?: DitherOptions,
): Promise<DitherResult> {
  const start = performance.now();
  const { source, regions, labelMap } = input;
  const { width: outW, height: outH } = source;

  if (regions.length === 0) {
    throw new Error('dither: regions must contain at least one entry');
  }
  if (labelMap && (labelMap.width !== outW || labelMap.height !== outH)) {
    throw new Error('dither: labelMap dimensions must match source');
  }

  // Initialise output as white. Pixels whose label has no matching region
  // remain at this default; the caller is responsible for ensuring regions
  // cover every label they care about.
  const output = new ImageData(outW, outH);
  for (let i = 0; i < output.data.length; i += 4) {
    output.data[i] = 255;
    output.data[i + 1] = 255;
    output.data[i + 2] = 255;
    output.data[i + 3] = 255;
  }

  let processedW = outW;
  let processedH = outH;

  // ---------------------------------------------------------------------------
  // B0.1 GPU dispatch hook -- FRAME-LEVEL, before the per-region crop loop.
  // B0.2 fills in real WebGL2 rendering; B0.6 adds real route planning.
  // When options.backend is absent the entire block is skipped and the
  // existing CPU path runs unchanged, preserving byte-identical output.
  // ---------------------------------------------------------------------------
  if (options?.backend !== undefined) {
    const routeOptions: RoutePlannerOptions = {
      backend: options.backend,
      ...(options.__routeOverride !== undefined
        ? { __routeOverride: options.__routeOverride }
        : {}),
      ...(labelMap !== undefined ? { labelMap } : {}),
    };
    const plan = planFrameRoute(regions, source, routeOptions);

    if (plan.defaultBackend === 'mixed' && labelMap === undefined) {
      const fallbackReasonByLabel = new Map(plan.reasonByLabel);
      for (const label of plan.gpuRegionLabels) {
        fallbackReasonByLabel.set(
          label,
          'mixed GPU/CPU plan requires labelMap in B1',
        );
      }
      const diagnostics: GpuRenderDiagnostics = {
        backend: 'cpu',
        readbackCount: 0,
        renderedLabels: [],
        fallbackLabels: regions.map(r => r.label),
        fallbackReasonByLabel: Object.fromEntries(fallbackReasonByLabel),
      };
      options.onGpuFallback?.(diagnostics);
      // Continue to the CPU path below.
    } else if (
      plan.defaultBackend === 'gpu-webgl2' ||
      plan.defaultBackend === 'mixed'
    ) {
      // Some or all regions are routed to GPU.
      const plannedRegions = regions
        .filter(r => plan.gpuRegionLabels.includes(r.label))
        .map((r: Region) => ({ label: r.label, method: r.method }));

      const req: GpuRegionBatchRequest = {
        source,
        ...(labelMap !== undefined ? { labelMap } : {}),
        regions: plannedRegions,
        readbackStrategy: 'single-frame-production',
      };

      try {
        const result = await options.backend.renderRegionBatch(req);
        if (result.output.width !== outW || result.output.height !== outH) {
          throw new Error(
            `GPU backend output dimensions ${result.output.width}x${result.output.height} do not match source ${outW}x${outH}`,
          );
        }
        // For B0.1 the fake backend pass-through is the full frame output
        // (route stub forces ALL or NONE -- no partial compositing needed).
        output.data.set(result.output.data);

        if (plan.defaultBackend === 'mixed') {
          const diagnostics: GpuRenderDiagnostics = {
            backend: 'mixed',
            attemptedBackend: result.diagnostics.backend,
            readbackCount: result.diagnostics.readbackCount,
            renderedLabels: plan.gpuRegionLabels,
            fallbackLabels: plan.cpuRegionLabels,
            fallbackReasonByLabel: Object.fromEntries(plan.reasonByLabel),
            ...(result.diagnostics.parityMode !== undefined
              ? { parityMode: result.diagnostics.parityMode }
              : {}),
          };
          options.onGpuFallback?.(diagnostics);

          const cpuRegions = regions.filter(r => plan.cpuRegionLabels.includes(r.label));
          await compositeCpuLabelRegions(output, source, labelMap!, cpuRegions, options);
          applyAlphaForLabelZero(output, labelMap!.data, outW, outH);

          return {
            output,
            processedSize: { w: outW, h: outH },
            outputSize: { w: outW, h: outH },
            elapsedMs: performance.now() - start,
          };
        }

        if (labelMap !== undefined) {
          applyAlphaForLabelZero(output, labelMap.data, outW, outH);
        }

        const diagnostics: GpuRenderDiagnostics = {
          backend: result.diagnostics.backend,
          ...(result.diagnostics.attemptedBackend !== undefined
            ? { attemptedBackend: result.diagnostics.attemptedBackend }
            : {}),
          readbackCount: result.diagnostics.readbackCount,
          renderedLabels: plan.gpuRegionLabels,
          fallbackLabels: [],
          ...(result.diagnostics.parityMode !== undefined
            ? { parityMode: result.diagnostics.parityMode }
            : {}),
        };

        // onGpuFallback is NOT called on the pure GPU success path.
        // Only call it when the backend field indicates CPU ran.
        const b = diagnostics.backend;
        if (
          b === 'fallback-cpu-after-gpu-error' ||
          b === 'cpu' ||
          b === 'mixed'
        ) {
          options.onGpuFallback?.(diagnostics);
        }

        return {
          output,
          processedSize: { w: outW, h: outH },
          outputSize: { w: outW, h: outH },
          elapsedMs: performance.now() - start,
        };
      } catch (err) {
        // Runtime failure -- fall through to CPU oracle for all regions.
        const diagnostics: GpuRenderDiagnostics = {
          backend: 'fallback-cpu-after-gpu-error',
          attemptedBackend: backendNameToRenderBackend(options.backend.name),
          gpuError: err instanceof Error ? err.message : String(err),
          readbackCount: 0,
          renderedLabels: [],
          fallbackLabels: plan.gpuRegionLabels,
        };
        options.onGpuFallback?.(diagnostics);
        // Continue to the CPU path below (no early return).
      }
    } else {
      // Backend provided but route stub returned no GPU regions.
      const diagnostics: GpuRenderDiagnostics = {
        backend: 'cpu',
        readbackCount: 0,
        renderedLabels: [],
        fallbackLabels: plan.cpuRegionLabels,
        fallbackReasonByLabel: Object.fromEntries(plan.reasonByLabel),
      };
      options.onGpuFallback?.(diagnostics);
      // Continue to the CPU path below.
    }
  }

  if (!labelMap) {
    const first = regions[0]!;
    const result = await runMethodOnBuffer(source, first.method, options);
    processedW = result.processedW;
    processedH = result.processedH;
    output.data.set(result.output.data);
  } else {
    // Multi-region path. Each region runs only over the bounding box of its
    // labelled pixels, with absolute-coord origin threaded through so
    // ordered methods + Family B produce output byte-identical to a
    // full-image run. Error diffusion accepts a small contained behaviour
    // change at the bbox top / left because seq error accumulation depends
    // on buffer shape - but errors stay strictly inside the region anyway,
    // which is what multi-region was always supposed to mean.
    //
    // processedSize stays at source dimensions in the multi-region path -
    // bbox cropping is an internal optimisation, not a contract change for
    // the demo's status bar.
    const labelData = labelMap.data;
    await compositeCpuLabelRegions(output, source, labelMap, regions, options);

    // Item 25 - alpha=0 pass (plan A8).
    // After all region compositing is complete, zero the alpha channel for
    // every pixel whose label is 0. Label-0 pixels represent unlabelled or
    // deleted regions (see deleteRegions in region-ops.ts). Unmatched non-zero
    // labels are NOT touched - they retain their opaque white default, matching
    // the existing "unknown labels stay white" test contract.
    // This pass runs in the multi-region path only; the single-region path has
    // no labelMap and never invokes this step.
    applyAlphaForLabelZero(output, labelData, outW, outH);
  }

  return {
    output,
    processedSize: { w: processedW, h: processedH },
    outputSize: { w: outW, h: outH },
    elapsedMs: performance.now() - start,
  };
}
